import { DateTime } from "luxon";
import { AgGrid } from "@jsq/ds/core";
import { DefaultColumnTypes } from "#utils/agGrid.constants";
import { ActionButton } from "../../common/components/ActionButton";
import { actionButtonCellClass } from "../../common/components/ActionButton/styles_vanilla.css";
import { ClientFilter } from "../GridFilters/ClientFilter";
import { DistributionDateFilter } from "../GridFilters/DistributionDateFilter";
import { EntityFilter } from "../GridFilters/EntityFilter";
import Money from "../Money";
import { DistributionBatchRowItem } from "./types";

/**
 * List of the supported columns in the Distribution Batches table
 * (ordered from left to right)
 */
export enum SupportedColumns {
  ClientName = "clientName",
  EntityName = "entityName",
  DistributionDate = "distributionDate",
  Description = "description",
  PositionsCount = "positionsCount",
  DistributionAmount = "distributionAmount",
  RemainingToPayAmount = "remainingToPayAmount",
  Action = "action",
}

/**
 * The mapper provides the corresponding column name displayed in the table
 */
export const columnNames = new Map<SupportedColumns, string>([
  [SupportedColumns.ClientName, "Client"],
  [SupportedColumns.EntityName, "Entity"],
  [SupportedColumns.DistributionDate, "Distribution date"],
  [SupportedColumns.Description, "Description"],
  [SupportedColumns.PositionsCount, "Positions"],
  [SupportedColumns.DistributionAmount, "Amount"],
  [SupportedColumns.RemainingToPayAmount, "Remaining to pay"],
  [SupportedColumns.Action, "Action"],
]);

export type ActionColumnCallbacks = {
  onPayClick?: (distributionBatch: DistributionBatchRowItem) => void;
  onReviewClick?: (distributionBatch: DistributionBatchRowItem) => void;
};

/**
 * Base column definitions without the action column
 */
const baseColumnDefinitions: AgGrid.ColDef[] = [
  {
    field: SupportedColumns.ClientName,
    headerName: columnNames.get(SupportedColumns.ClientName),
    filter: ClientFilter,
    // Exclude filtering from the column header because UX isn't great
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
  },
  {
    field: SupportedColumns.EntityName,
    headerName: columnNames.get(SupportedColumns.EntityName),
    filter: EntityFilter,
    filterParams: {
      dependentFilter: SupportedColumns.ClientName,
    },
    // Exclude filtering from the column header because UX isn't great
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
  },
  {
    field: SupportedColumns.DistributionDate,
    headerName: columnNames.get(SupportedColumns.DistributionDate),
    filter: DistributionDateFilter,
    // Exclude filtering from the column header because UX isn't great
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
    sortable: true,
    sort: "desc",
    valueFormatter: ({ data }: { data: DistributionBatchRowItem }) => {
      return data.distributionDate.toLocaleString(DateTime.DATE_MED);
    },
  },
  {
    field: SupportedColumns.Description,
    headerName: columnNames.get(SupportedColumns.Description),
    minWidth: 260,
  },
  {
    field: SupportedColumns.PositionsCount,
    headerName: columnNames.get(SupportedColumns.PositionsCount),
    maxWidth: 150,
  },
  {
    field: SupportedColumns.DistributionAmount,
    headerName: columnNames.get(SupportedColumns.DistributionAmount),
    type: DefaultColumnTypes.numericColumn,
    maxWidth: 130,
    cellRenderer: Money,
    cellRendererParams: ({ data }: { data: DistributionBatchRowItem }) => ({
      money: data.distributionAmount,
      rounded: false,
    }),
  },
  {
    field: SupportedColumns.RemainingToPayAmount,
    headerName: columnNames.get(SupportedColumns.RemainingToPayAmount),
    type: DefaultColumnTypes.numericColumn,
    maxWidth: 200,
    resizable: false,
    cellRenderer: Money,
    cellRendererParams: ({ data }: { data: DistributionBatchRowItem }) => ({
      money: data.remainingToPayAmount,
      rounded: false,
    }),
  },
];

/**
 * Creates the action column definition with the provided callbacks
 */
const createActionColumnDef = (callbacks: ActionColumnCallbacks): AgGrid.ColDef => ({
  field: SupportedColumns.Action,
  headerName: columnNames.get(SupportedColumns.Action),
  pinned: "right",
  sortable: false,
  filter: false,
  resizable: false,
  flex: 0,
  width: 108,
  cellClass: actionButtonCellClass,
  cellRenderer: ActionButton,
  cellRendererParams: ({ data }: { data: DistributionBatchRowItem }) => ({
    action: data.action,
    onClick:
      data.action === "pay"
        ? () => callbacks.onPayClick?.(data)
        : data.action === "review"
          ? () => callbacks.onReviewClick?.(data)
          : undefined,
  }),
});

/**
 * Creates AG Grid Column definitions for the Distribution Batches table
 * @param callbacks - Optional callbacks for action button clicks
 */
export const getColumnDefinitions = (
  callbacks: ActionColumnCallbacks = {},
): AgGrid.ColDef[] => [...baseColumnDefinitions, createActionColumnDef(callbacks)];

/**
 * @deprecated Use getColumnDefinitions instead for action button click handling
 * AG Grid Column definitions for the Distribution Batches table
 */
export const columnDefinitions: AgGrid.ColDef[] = getColumnDefinitions();
